import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { useSEO } from '@/components/feature/useSEO';
import { articles } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  const relatedArticles = article
    ? articles.filter((a) => a.categoryId === article.categoryId && a.id !== article.id).slice(0, 3)
    : [];

  useSEO({
    title: article ? article.title : '文章未找到',
    description: article ? article.excerpt : '',
    keywords: article ? article.tags.join(',') : '',
    canonicalUrl: `https://utshealth.cn/article/${id}`,
    ogType: 'article',
    schema: article ? {
      '@context': 'https://schema.org',
      '@type': 'Article',
      'headline': article.title,
      'description': article.excerpt,
      'author': { '@type': 'Person', 'name': article.author },
      'datePublished': article.publishDate,
      'publisher': { '@type': 'Organization', 'name': '一起草', 'url': 'https://utshealth.cn' },
      'mainEntityOfPage': { '@type': 'WebPage', '@id': `https://utshealth.cn/article/${article.id}` },
    } : undefined,
  });

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: article.categoryName, to: `/category/${article.categoryId}` },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-snug mb-6">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-600 text-sm"></i>
                </div>
                <span className="font-medium text-foreground-700">{article.author}</span>
                <span className="text-foreground-400">· {article.authorRole}</span>
              </div>
              <span className="text-foreground-300">|</span>
              <div className="flex items-center gap-1">
                <i className="ri-calendar-line text-sm"></i>
                <span>{article.publishDate}</span>
              </div>
              <span className="text-foreground-300">|</span>
              <div className="flex items-center gap-1">
                <i className="ri-time-line text-sm"></i>
                <span>{article.readTime} 分钟阅读</span>
              </div>
            </div>
          </header>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.heroImage}
              alt={article.title}
              className="w-full h-[300px] md:h-[400px] object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none mb-12 article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="border-t border-background-200/70 pt-8 mb-16">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm text-foreground-500">分享到：</span>
              <a href="#" className="w-9 h-9 flex items-center justify-center bg-background-100 rounded-full text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors" rel="nofollow" aria-label="微信分享">
                <i className="ri-wechat-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center bg-background-100 rounded-full text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors" rel="nofollow" aria-label="微博分享">
                <i className="ri-weibo-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center bg-background-100 rounded-full text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors" rel="nofollow" aria-label="复制链接">
                <i className="ri-link"></i>
              </a>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="h-40 overflow-hidden">
                      <img
                        src={ra.cardImage}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {ra.title}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-foreground-400">
                        <span>{ra.author}</span>
                        <span>·</span>
                        <span>{ra.publishDate}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}